import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { MaskedInput } from '@/shared/components/masked-input';
import { maskCpf, maskCnpj } from '@/shared/lib/input-masks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { normalizeCarPlate } from '../model/car-plate';
import { INTL_PHONE_COUNTRIES } from '../model/intl-phone';
import type { IdentityField } from '../model/invite-variant';

interface PublicInviteIdentityFieldProps {
  identity: IdentityField;
  uniqueIdLabel: string;
  uniqueId: string;
  onUniqueIdChange: (value: string) => void;
  onUniqueIdBlur?: () => void;
  countryCode: string;
  onCountryCodeChange: (value: string) => void;
  phone: string;
  onPhoneChange: (value: string) => void;
}

/**
 * Campo(s) de identidade — a única coisa que muda de fato entre a maioria
 * das variações de loja (ver model/invite-variant.ts). `hidden-generated`
 * (abf/sqg) não renderiza nada aqui: o código já vem pronto do backend.
 */
export function PublicInviteIdentityField({
  identity,
  uniqueIdLabel,
  uniqueId,
  onUniqueIdChange,
  onUniqueIdBlur,
  countryCode,
  onCountryCodeChange,
  phone,
  onPhoneChange,
}: PublicInviteIdentityFieldProps) {
  if (identity === 'hidden-generated') return null;

  if (identity === 'cnpj') {
    return (
      <div className="space-y-2">
        <Label htmlFor="invite-unique-id">
          CNPJ <span className="text-destructive">*</span>
        </Label>
        <MaskedInput
          id="invite-unique-id"
          inputMode="numeric"
          placeholder="Preencha seu CNPJ"
          value={uniqueId}
          onValueChange={onUniqueIdChange}
          onBlur={onUniqueIdBlur}
          mask={maskCnpj}
        />
      </div>
    );
  }

  if (identity === 'car-plate') {
    return (
      <div className="space-y-2">
        <Label htmlFor="invite-unique-id">
          Placa do Carro <span className="text-destructive">*</span>
        </Label>
        <Input
          id="invite-unique-id"
          placeholder="Preencha a placa do carro"
          maxLength={7}
          style={{ textTransform: 'uppercase' }}
          value={uniqueId}
          onChange={(e) => onUniqueIdChange(normalizeCarPlate(e.target.value))}
          onBlur={onUniqueIdBlur}
        />
      </div>
    );
  }

  if (identity === 'intl-phone') {
    const selectedCountry = INTL_PHONE_COUNTRIES.find((c) => c.dialCode === countryCode);
    return (
      <div className="grid gap-4 sm:grid-cols-[minmax(0,9rem)_1fr]">
        <div className="space-y-2">
          <Label>DDI</Label>
          <Select value={countryCode} onValueChange={onCountryCodeChange}>
            <SelectTrigger>
              {/* Children explícitos: gatilho compacto ("🇵🇹 +351"), lista mostra o nome do país. */}
              <SelectValue placeholder="DDI">
                {selectedCountry ? `${selectedCountry.flag} +${selectedCountry.dialCode}` : undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {INTL_PHONE_COUNTRIES.map((c) => (
                <SelectItem key={c.iso2} value={c.dialCode}>
                  <span className="mr-1.5">{c.flag}</span>
                  {c.label} (+{c.dialCode})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-phone">
            Telemóvel <span className="text-destructive">*</span>
          </Label>
          <Input
            id="invite-phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="invite-unique-id">
        {uniqueIdLabel} <span className="text-destructive">*</span>
      </Label>
      <MaskedInput
        id="invite-unique-id"
        inputMode="numeric"
        placeholder={`Preencha seu ${uniqueIdLabel}`}
        value={uniqueId}
        onValueChange={onUniqueIdChange}
        onBlur={onUniqueIdBlur}
        mask={maskCpf}
      />
    </div>
  );
}
